const STATUS_REASON = Object.freeze({
  no_basis: "Sem base para calcular esta métrica.",
  degraded: "Dados incompletos impedem uma leitura confiável desta métrica.",
  stale: "Dados desatualizados; aguarde uma nova publicação.",
  source_unavailable: "Fonte de dados indisponível para esta métrica.",
  scope_violation: "Escopo de dados inválido para esta métrica.",
});

export const metricReasonStyle = Object.freeze({
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
});

const unavailableEnvelope = (envelope = {}, status = "source_unavailable") => ({
  id: envelope.id || "metric",
  value: null,
  unit: envelope.unit,
  status,
});

export const normalizeMetricEnvelope = (envelope = {}) => {
  if (!envelope || typeof envelope !== "object" || Array.isArray(envelope)) {
    return unavailableEnvelope();
  }
  const status =
    typeof envelope.status === "string"
      ? envelope.status
      : "source_unavailable";
  if (status !== "ok") return unavailableEnvelope(envelope, status);
  if (typeof envelope.value !== "number" || !Number.isFinite(envelope.value)) {
    return unavailableEnvelope(envelope, "degraded");
  }
  return {
    id: envelope.id || "metric",
    value: envelope.value,
    unit: envelope.unit,
    status: "ok",
  };
};

export const metricValue = (envelope) =>
  normalizeMetricEnvelope(envelope).value;

export const metricCsvValue = (envelope) => metricValue(envelope);

export const metricEnvelopeFor = (
  record,
  metricKey,
  legacyKey = metricKey,
  { id = metricKey, unit } = {},
) => {
  if (record?.metrics && Object.hasOwn(record.metrics, metricKey)) {
    return record.metrics[metricKey];
  }
  const legacyValue = record && Object.hasOwn(record, legacyKey)
    ? record[legacyKey]
    : undefined;
  const available = typeof legacyValue === "number" && Number.isFinite(legacyValue);
  return {
    id,
    unit,
    value: available ? legacyValue : null,
    status: available ? "ok" : "source_unavailable",
  };
};

export const combineMetricEnvelopes = (
  sources,
  derive,
  { id = "metric", unit } = {},
) => {
  const normalized = sources.map(normalizeMetricEnvelope);
  const unavailable = normalized.find(({ status }) => status !== "ok");
  if (unavailable) return { id, unit, value: null, status: unavailable.status };
  let value;
  try {
    value = derive(normalized.map((source) => source.value));
  } catch {
    value = Number.NaN;
  }
  if (value === null || value === undefined) {
    return { id, unit, value: null, status: "no_basis" };
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return { id, unit, value: null, status: "degraded" };
  }
  return { id, unit, value, status: "ok" };
};

export const deriveMetricEnvelope = (source, derive, { id, unit } = {}) => {
  const normalized = normalizeMetricEnvelope(source);
  const identity = {
    id: id || normalized.id,
    unit: unit || normalized.unit,
  };
  if (normalized.status !== "ok") {
    return { ...identity, value: null, status: normalized.status };
  }
  let value;
  try {
    value = derive(normalized.value);
  } catch {
    value = Number.NaN;
  }
  if (value === null || value === undefined) {
    return { ...identity, value: null, status: "no_basis" };
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return { ...identity, value: null, status: "degraded" };
  }
  return { ...identity, value, status: "ok" };
};

export const metricPresentation = (
  envelope,
  { formatter = String, reasonId } = {},
) => {
  const normalized = normalizeMetricEnvelope(envelope);
  if (normalized.status === "ok") {
    return {
      available: true,
      describedBy: undefined,
      reason: null,
      status: "ok",
      text: formatter(normalized.value),
      value: normalized.value,
    };
  }
  const status = Object.hasOwn(STATUS_REASON, normalized.status)
    ? normalized.status
    : "source_unavailable";
  return {
    available: false,
    describedBy: reasonId || `metric-${normalized.id}-${status}-reason`,
    reason: STATUS_REASON[status],
    status,
    text: "—",
    value: null,
  };
};

export const metricStatusReason = (status) =>
  STATUS_REASON[status] || STATUS_REASON.source_unavailable;
